import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { repairMojibakeText } from "./text";

const LANGUAGE_STORAGE_KEY = "learnova_language";

const translations = {
  ar: {
    appName: "Learnova",
    platformEyebrow: "منصة Learnova",
    brandTitle: "منصة تعليمية لإدارة التقييمات والتعلم",
    trainerDashboard: "لوحة المدرب",
    adminDashboard: "لوحة الإدارة",
    aiStudio: "استوديو الذكاء الاصطناعي",
    studentExams: "الاختبارات",
    logout: "تسجيل الخروج",
    trainerRole: "مدرب",
    adminRole: "مدير النظام",
    studentRole: "طالب",
    switchToArabic: "العربية",
    switchToEnglish: "English",
    loginHeroTitle: "ابنِ رحلة تعلم وتقييم أوضح للطالب والمدرب",
    loginHeroText: "سجل الدخول للوصول إلى الاختبارات والتحليلات ومتابعة التقدم بشكل منظم وسهل.",
    loginTitle: "تسجيل الدخول",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    loginButton: "تسجيل الدخول",
    noAccount: "ليس لديك حساب؟",
    registerLink: "إنشاء حساب",
    registerHeroTitle: "أنشئ حسابك وابدأ في إدارة الاختبارات والتعلم",
    registerHeroText: "سجل كطالب أو كمدرب، ثم نكمل بناء التجربة الكاملة خطوة بخطوة.",
    registerTitle: "إنشاء حساب",
    fullName: "الاسم الكامل",
    age: "العمر",
    birthDate: "تاريخ الميلاد",
    nationalId: "الرقم القومي",
    nationalIdHint: "يجب أن يكون 14 رقمًا",
    governorate: "المحافظة",
    phoneNumber: "رقم الهاتف",
    educationLevel: "المرحلة التعليمية",
    institutionName: "اسم الجامعة أو المدرسة أو جهة العمل",
    rememberMe: "تذكرني على هذا الجهاز",
    keepSignedInAfterRegister: "ابقَ مسجل الدخول بعد إنشاء الحساب",
    agreeToTerms: "أوافق على الشروط والأحكام وسياسة الخصوصية",
    termsRequired: "يجب الموافقة على الشروط والأحكام",
    selectGovernorate: "اختر المحافظة",
    selectEducationLevel: "اختر المرحلة التعليمية",
    invalidNationalId: "الرقم القومي يجب أن يكون 14 رقمًا",
    invalidAge: "العمر يجب أن يكون رقمًا صحيحًا",
    role: "الدور",
    gender: "النوع",
    student: "طالب",
    trainer: "مدرب",
    male: "ذكر",
    female: "أنثى",
    registerButton: "إنشاء حساب",
    haveAccount: "لديك حساب بالفعل؟",
    loginLink: "تسجيل الدخول",
    serverError: "حدث خطأ في الخادم"
  },
  en: {
    appName: "Learnova",
    platformEyebrow: "Learnova Platform",
    brandTitle: "A learning platform for assessments and progress tracking",
    trainerDashboard: "Trainer Dashboard",
    adminDashboard: "Admin Dashboard",
    aiStudio: "AI Studio",
    studentExams: "Exams",
    logout: "Log Out",
    trainerRole: "Trainer",
    adminRole: "Admin",
    studentRole: "Student",
    switchToArabic: "العربية",
    switchToEnglish: "English",
    loginHeroTitle: "Build a clearer learning and assessment journey for students and trainers",
    loginHeroText: "Sign in to access exams, analytics, and progress tracking in a simple and organized way.",
    loginTitle: "Login",
    email: "Email",
    password: "Password",
    loginButton: "Login",
    noAccount: "Don't have an account?",
    registerLink: "Register",
    registerHeroTitle: "Create your account and start managing learning and assessments",
    registerHeroText: "Join as a student or a trainer, then we can keep building the full experience step by step.",
    registerTitle: "Create Account",
    fullName: "Full Name",
    age: "Age",
    birthDate: "Date of Birth",
    nationalId: "National ID",
    nationalIdHint: "It must be exactly 14 digits",
    governorate: "Governorate",
    phoneNumber: "Phone Number",
    educationLevel: "Education Level",
    institutionName: "University, School, or Workplace",
    rememberMe: "Remember me on this device",
    keepSignedInAfterRegister: "Keep me signed in after creating the account",
    agreeToTerms: "I agree to the terms, conditions, and privacy policy",
    termsRequired: "You must agree to the terms and conditions",
    selectGovernorate: "Select governorate",
    selectEducationLevel: "Select education level",
    invalidNationalId: "National ID must be exactly 14 digits",
    invalidAge: "Age must be a valid whole number",
    role: "Role",
    gender: "Gender",
    student: "Student",
    trainer: "Trainer",
    male: "Male",
    female: "Female",
    registerButton: "Register",
    haveAccount: "Already have an account?",
    loginLink: "Login",
    serverError: "Server error"
  }
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === "en" ? "en" : "ar";
  });

  useEffect(() => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

  const value = useMemo(() => {
    const direction = language === "ar" ? "rtl" : "ltr";

    return {
      language,
      direction,
      setLanguage,
      toggleLanguage: () => setLanguage((current) => (current === "ar" ? "en" : "ar")),
      t: (key) => repairMojibakeText(translations[language][key] || key)
    };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }

  return context;
}
